/**
 * Login Modal Component
 *
 * Google ve Email Link ile giriş yapma modalı
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EMAIL_COOLDOWN_KEY = 'emailLoginCooldownUntil';
const EMAIL_COOLDOWN_EMAIL_KEY = 'emailLoginCooldownEmail';
const EMAIL_COOLDOWN_SECONDS = 60;
const SHOW_EMAIL_LOGIN = false; // Email login UI temporarily hidden

export function LoginModal({ isOpen, onClose }: LoginModalProps) {
  const { signInWithGoogle, signInWithApple, sendEmailLink, error, isGoogleAuthInProgress } = useAuth();
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Load cooldown from localStorage (persist between refreshes)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const storedUntil = window.localStorage.getItem(EMAIL_COOLDOWN_KEY);
    const storedEmail = window.localStorage.getItem(EMAIL_COOLDOWN_EMAIL_KEY);
    if (storedEmail) {
      setEmail(storedEmail);
    }
    if (storedUntil) {
      const expiresAt = Number(storedUntil);
      const remaining = Math.ceil((expiresAt - Date.now()) / 1000);
      if (remaining > 0) {
        setCooldown(remaining);
        setEmailSent(true);
      } else {
        window.localStorage.removeItem(EMAIL_COOLDOWN_KEY);
        window.localStorage.removeItem(EMAIL_COOLDOWN_EMAIL_KEY);
      }
    }
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  useEffect(() => {
    if (cooldown === 0 && typeof window !== 'undefined') {
      window.localStorage.removeItem(EMAIL_COOLDOWN_KEY);
      window.localStorage.removeItem(EMAIL_COOLDOWN_EMAIL_KEY);
    }
  }, [cooldown]);

  if (!isOpen) return null;

  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true);
      await signInWithGoogle();
      onClose();
    } catch (err) {
      console.error('Google giriş hatası:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailLink = async () => {
    if (!email || !email.includes('@')) {
      alert('Geçerli bir email adresi girin');
      return;
    }

    if (cooldown > 0) {
      return;
    }

    if (typeof window !== 'undefined') {
      const storedUntil = window.localStorage.getItem(EMAIL_COOLDOWN_KEY);
      if (storedUntil) {
        const expiresAt = Number(storedUntil);
        const remaining = Math.ceil((expiresAt - Date.now()) / 1000);
        if (remaining > 0) {
          setCooldown(remaining);
          const storedEmail = window.localStorage.getItem(EMAIL_COOLDOWN_EMAIL_KEY);
          if (storedEmail && (email.trim() === '' || email === storedEmail)) {
            setEmail(storedEmail);
          }
          setEmailSent(true);
          return;
        }
      }
    }

    try {
      setIsLoading(true);
      await sendEmailLink(email);
      setEmailSent(true);
      setCooldown(EMAIL_COOLDOWN_SECONDS); // Kullanıcıyı kısa süre beklet
      if (typeof window !== 'undefined') {
        const expiresAt = Date.now() + EMAIL_COOLDOWN_SECONDS * 1000;
        window.localStorage.setItem(EMAIL_COOLDOWN_KEY, expiresAt.toString());
        window.localStorage.setItem(EMAIL_COOLDOWN_EMAIL_KEY, email);
      }
    } catch (err) {
      console.error('Email gönderme hatası:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative animate-scale-in">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Kapat"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="relative mx-auto mb-4 w-16 h-16">
            <div className="absolute -inset-1 bg-gradient-to-r from-accent-primary to-accent-secondary rounded-full blur opacity-30" />
            <div className="relative w-16 h-16 rounded-full shadow-lg overflow-hidden bg-white flex items-center justify-center">
              <img src="/Arıza.webp" alt="ArızaRehberim" className="w-full h-full object-contain" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Giriş Yapın</h2>
          <p className="text-gray-600 text-sm">
            Konuşmanızı kaydetmek ve daha fazla özellik için giriş yapın
          </p>
        </div>

        <div className="space-y-4">
          {/* Google popup açık uyarısı */}
          {isGoogleAuthInProgress && (
            <div className="p-4 bg-blue-50 border-2 border-blue-300 rounded-xl animate-fade-in">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                </div>
                <div className="text-left flex-1">
                  <p className="text-sm font-semibold text-blue-900 mb-1">Google giriş penceresi açık</p>
                  <p className="text-sm text-blue-700">
                    Lütfen açılan Google giriş penceresinde hesabınızı seçin. Pencereyi kaybettiyseniz, tarayıcınızın sekmeleri arasında arayın veya Google butonuna tekrar tıklayın.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Google Sign In */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {isGoogleAuthInProgress ? 'Google penceresi açık...' : 'Google ile Giriş Yap'}
          </button>

          {/* Apple Sign In */}
          <button
            type="button"
            onClick={async () => {
              try {
                setIsLoading(true);
                await signInWithApple();
                onClose();
              } catch (err) {
                console.error('Apple giriş hatası:', err);
              } finally {
                setIsLoading(false);
              }
            }}
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-black text-white border-2 border-black rounded-lg hover:bg-gray-900 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.127 3.675-.552 9.127 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
            </svg>
            Apple ile Giriş Yap
          </button>

          {/* Legal notice — implied consent on sign-in */}
          <p className="text-xs text-center text-gray-400 leading-relaxed pt-1">
            Giriş yaparak{' '}
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-gray-600 transition-colors"
            >
              Kullanım Koşulları
            </a>
            'nı ve{' '}
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-gray-600 transition-colors"
            >
              Gizlilik Politikası
            </a>
            'nı kabul etmiş olursunuz.
          </p>

          {SHOW_EMAIL_LOGIN && (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">veya</span>
                </div>
              </div>

              {/* Email Link */}
              <div className="space-y-3">
                <input
                  type="email"
                  placeholder="Email adresiniz"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleEmailLink();
                    }
                  }}
                  disabled={isLoading}
                  maxLength={254}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base text-gray-900 placeholder:text-gray-400 disabled:opacity-50"
                />
                <button
                  type="button"
                  onClick={handleEmailLink}
                  disabled={isLoading || cooldown > 0}
                  className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading
                    ? 'Gönderiliyor...'
                    : cooldown > 0
                      ? `${cooldown} sn sonra tekrar dene`
                      : 'Email ile Giriş Yap'}
                </button>
              </div>

              {emailSent && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl animate-fade-in">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-semibold text-green-800 mb-1">Email gönderildi!</p>
                      <p className="text-sm text-green-700">
                        <strong>{email}</strong> adresine giriş linki gönderdik. Lütfen emailini kontrol edip linke tıkla, sonra bu sekmeye geri dön.
                      </p>
                      <p className="text-sm text-green-700 mt-1">
                        Email gelmediyse <span className="font-semibold">Spam/Junk</span> klasörünü de kontrol et.
                      </p>
                      <button
                        onClick={() => {
                          setEmailSent(false);
                          setEmail('');
                        }}
                        className="mt-2 text-xs text-green-700 underline hover:text-green-800"
                      >
                        Farklı email dene
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-500 text-center">
                Email adresinize gönderilen bağlantı ile güvenli giriş yapın. Şifre yok!
              </p>
            </>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
